'use client'

import { useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { useTranslations } from '@/lib/next-shim'

import api from '@/lib/api'
import { useGetMyInfosQuery, useUpdateMyInfosMutation } from '@/core/graphql/hooks'
import { useCustomFormWithReset } from '@/hooks/useCustomFormWithReset'

import { stripEmptyStrings } from '../lib/stripEmptyStrings'
import { SettingsFormData, getPatientDefaultValues, mainInfo } from './profileForms'

export function useProfileMainCard() {
  const t = useTranslations()
  const { enqueueSnackbar } = useSnackbar()

  const { data } = useGetMyInfosQuery()
  const patient = data?.myInfos

  const [updateInfos, { loading }] = useUpdateMyInfosMutation()

  const [mode, setMode] = useState<'read' | 'edit'>('read')
  const [identityOtp, setIdentityOtp] = useState<{ open: boolean; sentTo: string }>({ open: false, sentTo: '' })
  const [requestingEdit, setRequestingEdit] = useState(false)

  const onRequestEdit = async () => {
    if (requestingEdit) return
    setRequestingEdit(true)
    try {
      const { sentTo } = await api.auth.sendIdentityChangeOtp()
      setIdentityOtp({ open: true, sentTo })
    } catch {
      enqueueSnackbar(t('page.AccountSettings.profile.snackbar.saveError'), { variant: 'error' })
    } finally {
      setRequestingEdit(false)
    }
  }

  const onIdentityOtpClose = () => setIdentityOtp({ open: false, sentTo: '' })

  const onIdentityOtpConfirmed = () => {
    setIdentityOtp({ open: false, sentTo: '' })
    setMode('edit')
  }

  const { control, handleSubmit } = useCustomFormWithReset<SettingsFormData, typeof patient>(patient, getPatientDefaultValues, mainInfo)

  const onSubmit: SubmitHandler<SettingsFormData> = async (formData) => {
    try {
      await updateInfos({ variables: { payload: stripEmptyStrings({ ...formData, birthday: new Date(formData.birthday).toISOString() }) } })
      enqueueSnackbar(t('page.AccountSettings.profile.snackbar.saveSuccess'), { variant: 'success' })
      setMode('read')
    } catch {
      enqueueSnackbar(t('page.AccountSettings.profile.snackbar.saveError'), { variant: 'error' })
    }
  }

  return {
    control,
    identityOtp,
    loading,
    mode,
    onIdentityOtpClose,
    onIdentityOtpConfirmed,
    onMainInfoSubmit: handleSubmit(onSubmit),
    onRequestEdit,
    requestingEdit,
  }
}
